import { CircleAlert, CircleCheckBig, CircleHelp, CircleMinus, Mail, RefreshCw, TriangleAlert } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { refreshSenderHealth, updateEmailLimits } from '../../api/endpoints';
import type { Inbox } from '../../api/types';
import { isManagedInbox, needsReauth, providerLine } from '../../lib/channelMeta';
import { useTimeFormat } from '../../lib/tenantTime';
import { sinceLabel } from './automationTools';
import { type CheckState, campaignSenders, capLine, healthChip, recordChecks } from './campaignSettingsTools';
import { useComms } from './context';
import { useConfigNav } from './useConfigNav';

/**
 * Whether each mailbox is a safe place to send a campaign FROM — the four
 * things a receiver checks, what to fix, and the one control that acts on the
 * answer, the daily cap.
 *
 * The same assessment the mailbox's own settings show (InboxManage), gathered
 * here because a campaign is where it matters: an operator choosing which
 * mailbox to send five thousand emails from wants every mailbox side by side.
 * It WARNS and never blocks, and `unknown` is never rendered as a pass — the
 * whole check exists to prevent a silent all-clear.
 */
export function SenderHealthSection({ focusInboxId }: { focusInboxId: number | null }) {
  const { inboxes, inboxesLoading } = useComms();
  const mailboxes = campaignSenders(inboxes).email;
  if (inboxesLoading && !mailboxes.length) return <div className="cpset-card cpset-pad cpset-muted">Loading…</div>;
  if (!mailboxes.length) {
    return (
      <div className="cpset-card cpset-pad cpset-empty">
        <b>No mailbox connected</b>
        <span className="cpset-hint">
          Sender health is checked for every mailbox a campaign can send from. Connect one under Senders.
        </span>
      </div>
    );
  }
  return (
    <>
      <p className="cpset-lead">
        What receivers can check about each mailbox before they decide where a campaign lands. Nothing here stops a
        send — it is what to fix, and how hard to hold a mailbox back until you have. Checked every day.
      </p>
      {mailboxes.map((inbox) => (
        <MailboxHealth key={inbox.id} inbox={inbox} focused={inbox.id === focusInboxId} />
      ))}
    </>
  );
}

const STATE_ICON: Record<CheckState, typeof CircleCheckBig> = {
  ok: CircleCheckBig,
  warn: TriangleAlert,
  risk: CircleAlert,
  unknown: CircleHelp,
  na: CircleMinus,
};

function MailboxHealth({ inbox, focused }: { inbox: Inbox; focused: boolean }) {
  const { reloadInboxes } = useComms();
  const { fmtDateTime } = useTimeFormat();
  const configNav = useConfigNav();
  const ref = useRef<HTMLElement>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cap, setCap] = useState('');
  const [saved, setSaved] = useState(false);

  // Opened from a Senders row's chip: bring that mailbox into view.
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [focused]);

  if (inbox.channelType !== 'email') return null;
  const managed = isManagedInbox(inbox);
  const address = inbox.channel.email ?? inbox.name;

  if (!managed) {
    return (
      <section ref={ref} className="cpset-card cpset-pad cpset-mailbox">
        <MailboxHead address={address} sub={providerLine(inbox)} />
        <span className="cpset-hint">
          Only somebody who manages this mailbox can see its sender health and change its daily limit.
        </span>
      </section>
    );
  }

  const channel = inbox.channel;
  const health = channel.senderHealth;
  const chip = healthChip(health);
  const checks = health ? recordChecks(health, channel.provider) : [];
  const findings = health ? health.domainAuth.findings : [];

  const recheck = async () => {
    setBusy(true);
    setError(null);
    try {
      await refreshSenderHealth(channel.channelId);
      // The endpoint writes the assessment onto the row; reload rather than
      // holding a second copy, so this, the mailbox's settings and the builder
      // all read the same one.
      reloadInboxes();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The check could not be run.');
    } finally {
      setBusy(false);
    }
  };

  const applyCap = async (value: number | null) => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateEmailLimits(channel.channelId, { dailySendLimit: value });
      setSaved(true);
      setCap('');
      reloadInboxes();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The limit could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const typed = cap.trim();
  const parsed = typed === '' ? null : Number.parseInt(typed, 10);
  const capValid = typed !== '' && Number.isInteger(parsed) && (parsed as number) >= 0;
  const suggested = health?.suggestedDailyLimit ?? null;

  return (
    <section ref={ref} className="cpset-card cpset-mailbox" aria-label={`Sender health for ${address}`}>
      <div className="cpset-pad cpset-mailbox-top">
        <MailboxHead address={address} sub={providerLine(inbox)}>
          <span className={`cpset-chip ${chip.state}`}>{chip.label}</span>
        </MailboxHead>

        {needsReauth(inbox) && (
          <div className="cmg-insight bad">
            <CircleAlert size={15} />
            <span>
              This mailbox needs signing in again, so nothing sends from it until it is.{' '}
              <button type="button" className="cmg-link-btn" onClick={() => configNav(`/inboxes/${inbox.id}`)}>
                Reconnect
              </button>
            </span>
          </div>
        )}

        {!health ? (
          <span className="cpset-hint">
            Not checked yet. This runs shortly after a mailbox is connected and again every day; use Check now if you
            would rather not wait.
          </span>
        ) : (
          <div className="cpset-checks">
            {checks.map((check) => {
              const Icon = STATE_ICON[check.state];
              return (
                <div key={check.id} className={`cpset-check ${check.state}`}>
                  <Icon size={16} aria-hidden />
                  <span className="cpset-check-label">{check.label}</span>
                  <span className="cpset-check-value">{check.value}</span>
                </div>
              );
            })}
          </div>
        )}

        {health?.level === 'unknown' && (
          <span className="cpset-hint">
            We could not finish checking this mailbox, so treat what is above as incomplete rather than as a clean
            bill.
          </span>
        )}
      </div>

      {findings.length > 0 && (
        <ul className="cpset-findings">
          {findings.map((finding) => (
            <li key={finding.id} className={finding.level}>
              <b>{finding.title}.</b> {finding.detail}
            </li>
          ))}
        </ul>
      )}

      {/* The one control that acts on all of the above. Zero is accepted and
          means this mailbox sends nothing — the honest bottom of the scale. */}
      <div className="cpset-pad cpset-cap">
        <div className="cpset-cap-text">
          <b id={`cap-${inbox.id}`}>Daily sending limit</b>
          <span className="cpset-hint">
            {capLine(channel.dailySendLimit)}{' '}
            {suggested
              ? `For a mailbox in this shape, start around ${suggested} a day and raise it over a few weeks.`
              : 'Any number is honoured, including 0, which stops sending from this mailbox.'}
          </span>
        </div>
        <div className="cpset-inline">
          <input
            className="cmg-input cpset-cap-input"
            type="number"
            min={0}
            step={1}
            value={cap}
            aria-labelledby={`cap-${inbox.id}`}
            placeholder={channel.dailySendLimit != null ? String(channel.dailySendLimit) : 'Provider default'}
            onChange={(e) => {
              setCap(e.target.value);
              setSaved(false);
            }}
          />
          <button type="button" className="cbtn" disabled={saving || !capValid} onClick={() => void applyCap(parsed)}>
            Save
          </button>
          {suggested !== null && suggested !== channel.dailySendLimit && (
            <button type="button" className="cbtn" disabled={saving} onClick={() => void applyCap(suggested)}>
              Use {suggested}/day
            </button>
          )}
          {channel.dailySendLimit != null && (
            <button type="button" className="cbtn" disabled={saving} onClick={() => void applyCap(null)}>
              Back to the provider’s
            </button>
          )}
        </div>
        {typed !== '' && !capValid && (
          <span className="cpset-hint cpset-warn-text">Enter a whole number of 0 or more.</span>
        )}
        {saved && <span className="cpset-hint cpset-ok-text">Saved.</span>}
      </div>

      <div className="cpset-pad cpset-mailbox-foot">
        <button type="button" className="cbtn" onClick={() => void recheck()} disabled={busy}>
          <RefreshCw size={13} />
          {busy ? 'Checking…' : 'Check now'}
        </button>
        {channel.senderHealthAt && (
          <span className="cpset-hint">Last checked {sinceLabel(channel.senderHealthAt, fmtDateTime)}</span>
        )}
        {error && <span className="cpset-hint cpset-warn-text">{error}</span>}
      </div>
    </section>
  );
}

function MailboxHead({ address, sub, children }: { address: string; sub: string; children?: ReactNode }) {
  return (
    <div className="cpset-mailbox-head">
      <span className="cpset-sender-icon" aria-hidden>
        <Mail size={16} />
      </span>
      <div className="cpset-sender-text">
        <b>{address}</b>
        <span>{sub}</span>
      </div>
      {children}
    </div>
  );
}
